import { computed, makeObservable, observable } from 'mobx';
import { PageTemplateCategory, SectionCategory } from '../add_panel/content';

export type Route = Readonly<
  | { path: 'edit' }
  | { path: 'edit/replace-component' }
  | { path: 'edit/schemes' }
  | { path: 'edit/images' }
  | AddRoute
  | { path: 'styleguide' }
>;

export type AddRoute =
  | { path: 'add'; search?: undefined; filter?: undefined }
  | { path: 'add/page-categories'; search?: undefined; filter?: undefined }
  | {
      path: 'add/search';
      search: string;
      filter:
        | {
            type: 'sections';
            category?: SectionCategory;
          }
        | {
            type: 'pages';
            category?: PageTemplateCategory;
          };
    };

export type Toplevel = 'edit' | 'add' | 'styleguide';

export class SidebarNavigation {
  at: Route | undefined = undefined;

  constructor() {
    makeObservable(this, {
      at: observable.ref,
      toplevel: computed,
      back: computed,
    });
  }

  get toplevel(): Toplevel | undefined {
    if (!this.at) return undefined;
    return TOPLEVELS_BY_PATH[this.at.path];
  }

  get back(): (() => void) | undefined {
    const { goesBackTo } = this;
    return goesBackTo ? () => this.goto(goesBackTo) : undefined;
  }

  get goesBackTo(): Route | undefined {
    if (!this.at) return undefined;
    switch (this.at.path) {
      case 'edit':
        return undefined;
      case 'edit/replace-component':
      case 'edit/schemes':
      case 'edit/images':
        return { path: 'edit' };
      case 'add':
        return undefined;
      case 'add/page-categories':
        return { path: 'add' };
      case 'add/search':
        if (this.at.filter?.type === 'sections') {
          return { path: 'add' };
        } else {
          return { path: 'add/page-categories' };
        }
    }
  }

  goto(route: Route): void {
    this.at = route;
  }

  toggleToplevel(toplevel: Toplevel): void {
    this.at = this.at?.path === toplevel ? undefined : { path: toplevel };
  }

  readonly close = () => {
    this.at = undefined;
  };

  readonly backOrClose = () => {
    if (this.back) {
      this.back();
    } else {
      this.close();
    }
  };

  /** Don't navigate if the edit panel is already open **/
  readonly openEditPanelIfClosed = () => {
    if (this.at == null || this.toplevel !== 'edit') {
      this.goto({ path: 'edit' });
    }
  };
}

const TOPLEVELS_BY_PATH: Record<Route['path'], Toplevel> = {
  edit: 'edit',
  'edit/replace-component': 'edit',
  'edit/schemes': 'edit',
  'edit/images': 'edit',
  add: 'add',
  'add/page-categories': 'add',
  'add/search': 'add',
  styleguide: 'styleguide',
};
